<template>
  <div class="stop-loss-calculator-container">
    <PageHeader
      title="Calculadora de Stop Loss"
      subtitle="Herramienta para determinar el precio de Stop Loss de tus valores según la metodología Eurekers."
    />

    <div class="main-content-card card">
      <div class="form-section">
        <div class="form-group">
          <label for="sl-price-input">Precio de la Acción (€)</label>
          <input 
            type="text" 
            id="sl-price-input" 
            class="form-input" 
            v-model="price_str"
            @input="handleNumericInput('price_str')"
            placeholder="Ej: 100.00"
          >
        </div>

        <div class="form-group toggle-group">
          <span class="toggle-label">Porcentaje de Stop Loss:</span>
          <div class="toggle-switch-wrapper">
            <span :class="{'active-toggle-option': !is_ipo_percentage}" @click="is_ipo_percentage = false">2%</span>
            <label class="toggle-switch">
              <input type="checkbox" v-model="is_ipo_percentage">
              <span class="slider round"></span>
            </label>
            <span :class="{'active-toggle-option': is_ipo_percentage}" @click="is_ipo_percentage = true">3% (IPO/Reciente)</span>
          </div>
        </div>
      </div>

      <div class="results-section" v-if="price_str !== '' && parsed_price > 0">
        <h3 class="typo-headline-6 section-title">Resultado del Stop Loss</h3>
        <div class="result-box">
          <p class="result-label">Precio de Stop Loss Sugerido</p>
          <p class="result-value result-highlight">{{ calculated_stop_loss_processed }}</p>
        </div>
         <small class="result-detail">
            Este es el precio al que deberías considerar vender para limitar tus pérdidas potenciales.<br>
            Para un 2%: {{ calculateStopPrice(parsed_price, false) }} €.<br>
            Para un 3%: {{ calculateStopPrice(parsed_price, true) }} €.
        </small>
      </div>
       <div class="results-section placeholder-results" v-else>
        <p class="typo-body-1">Introduce un precio para calcular el Stop Loss.</p>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/shared/components/common/PageHeader.vue";

export default {
  name: 'StopLossCalculator', // Renamed for clarity
  components: {
    PageHeader,
  },
  metaInfo() {
    return {
      title: 'Calculadora de Stop Loss Eurekers',
      meta:[
        { name: 'description', content: "Calcula el precio de Stop Loss para tus inversiones con la herramienta de Eurekers."}
      ]
    }
  },
  data() {
    return {
      price_str: '', // Input string for price
      is_ipo_percentage: false, // false for 2%, true for 3%
    };
  },
  computed: {
    parsed_price() {
      return this._getParsedNumericValue(this.price_str);
    },
    calculated_stop_loss() {
      if (this.parsed_price <= 0) return 0;
      return this.calculateStopPrice(this.parsed_price, this.is_ipo_percentage);
    },
    calculated_stop_loss_processed() {
        return this.formatCurrency(this.calculated_stop_loss);
    }
  },
  methods: {
    handleNumericInput(fieldName) {
      let value = this[fieldName];
      if (typeof value !== 'string') value = String(value);
      value = value.replace(/[^0-9.,]/g, '');
      const parts = value.split(/[.,]/);
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      } else if (parts.length === 2) {
        value = parts[0] + '.' + parts[1];
      }
      this[fieldName] = value;
    },
    _getParsedNumericValue(value) {
      if (value === null || value === undefined || String(value).trim() === '') return 0;
      const sValue = String(value).replace(',', '.');
      const num = parseFloat(sValue);
      return isNaN(num) ? 0 : num;
    },
    formatCurrency(value) {
        const num = Number(value);
        if (isNaN(num)) return "0,00 €";
        return num.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    calculateStopPrice(price, is_ipo) {
      if (price <= 0) return 0;
      const filter_percentage = is_ipo ? 0.03 : 0.02;
      let result = price * (1 - filter_percentage);
      
      // Round to 2 decimal places for financial values
      result = Math.round(result * 100) / 100;

      // Specific Eurekers rounding rule: if ends in .x0 or .x5, subtract 0.01
      // Convert to string to check the last digit of the decimal part
      let result_str = result.toFixed(2);
      const last_decimal_digit = result_str.slice(-1);

      if (last_decimal_digit === "0" || last_decimal_digit === "5") {
        result -= 0.01;
      }
      // Ensure result is not negative after adjustment, and format to 2 decimal places
      return Math.max(0, parseFloat(result.toFixed(2))); 
    },
  }
}
</script>

<style lang="scss" scoped>
.stop-loss-calculator-container {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  padding: 20px 5%;
  min-height: 100vh;
  max-width: 700px; // Adjusted max-width for a more focused layout
  margin: 0 auto;
  box-sizing: border-box;
}

.card {
  background-color: var(--color-surface);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  padding: 30px 35px;
  margin-top: 20px; // Space below PageHeader
  border: 1px solid var(--color-outline);
}

.form-section {
  margin-bottom: 30px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;

  label:not(.toggle-switch) { // Exclude toggle switch's label from this styling
    display: block;
    margin-bottom: 8px;
    font-size: 0.875rem; // 14px
    font-weight: 500;
    color: var(--color-text-secondary);
  }
}

.form-input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--color-outline);
  border-radius: 6px;
  background-color: var(--color-input-background, var(--color-surface));
  color: var(--color-text-primary);
  font-size: 1rem; // 16px
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(15, 174, 215, 0.25));
  }

  &::placeholder {
    color: var(--color-text-placeholder, var(--color-text-inactive));
    opacity: 0.7;
  }
}

.toggle-group {
  align-items: flex-start; // Align items to the start for better label positioning
  .toggle-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-text-secondary);
    margin-bottom: 10px;
  }
}

.toggle-switch-wrapper {
  display: flex;
  align-items: center;
  gap: 12px; // Space between elements
  background-color: var(--color-surface-variant, var(--color-background));
  padding: 8px 12px;
  border-radius: 20px; // Pill shape for the wrapper
  border: 1px solid var(--color-outline-variant);
  width: fit-content;

  span:not(.slider) { // Style for 2% and 3% text
    font-size: 0.875rem;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 16px;
    transition: background-color 0.2s ease, color 0.2s ease;
    user-select: none;
    &.active-toggle-option {
      color: var(--color-text-on-highlight);
      background-color: var(--color-highlight);
      font-weight: 500;
    }
  }
}

/* The switch - the box around the slider */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px; // Width of the toggle
  height: 28px; // Height of the toggle

  /* Hide default HTML checkbox */
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-outline); // Off state color
  transition: .4s;
  border-radius: 28px; // Makes it round

  &:before {
    position: absolute;
    content: "";
    height: 20px; // Size of the knob
    width: 20px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
}

input:checked + .slider {
  background-color: var(--color-outline);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--color-primary);
}

input:checked + .slider:before {
  transform: translateX(22px); // Movement distance of the knob
}

.results-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--color-outline-variant);

  &.placeholder-results {
    text-align: center;
    color: var(--color-text-secondary);
    padding: 20px 0;
  }
}

.section-title {
  color: var(--color-text-primary);
  margin-top: 0;
  margin-bottom: 15px;
}

.result-box {
  background-color: var(--color-active-surface, var(--color-surface-variant));
  padding: 15px 20px;
  border-radius: 8px;
  border: 1px solid var(--color-outline-variant, var(--color-outline));
  margin-bottom: 10px;

  .result-label {
    font-size: 0.8125rem; // 13px
    font-weight: 600;
    color: var(--color-text-secondary);
    margin-bottom: 5px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .result-value {
    font-size: 1.8rem; // Larger result values
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0 0 5px 0;
    line-height: 1.2;

    &.result-highlight {
      color: var(--color-primary); // Emphasize key result
    }
  }
}

.result-detail {
    font-size: 0.8rem;
    color: var(--color-text-secondary);
    line-height: 1.5;
    display: block;
    margin-top: 10px;
}


.typo-headline-6 { font-size: 1.25rem; font-weight: 500; line-height: 1.4; }
.typo-body-1 { font-size: 1rem; line-height: 1.6; }

// Responsive adjustments
@media (max-width: 600px) {
  .card {
    padding: 20px 25px;
  }
  .toggle-switch-wrapper {
    width: 100%;
    justify-content: space-between;
    span:not(.slider) { flex-grow: 1; text-align: center; }
  }
  .result-value {
    font-size: 1.6rem;
  }
}
</style>
